/* eslint-disable */
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import { useTranslation } from "react-i18next";

/**
 * Estilos del diálogo de detalle.
 *
 * Objetivos:
 * - mantener estética limpia y consistente
 * - evitar fullScreen en móvil/tablet
 * - dejar visible parte del fondo también en pantallas pequeñas
 * - asegurar legibilidad y wrap correcto en textos largos
 * - evitar desbordes horizontales
 * - permitir scroll interno del contenido, no de toda la pantalla
 */
const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    width: "min(720px, calc(100vw - 32px))",
    maxWidth: "720px",
    maxHeight: "calc(100vh - 32px)",
    margin: theme.spacing(2),
    borderRadius: 18,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",

    [theme.breakpoints.down("sm")]: {
      width: "calc(100vw - 24px)",
      maxHeight: "calc(100vh - 24px)",
      margin: theme.spacing(1.5),
      borderRadius: 16,
    },

    [theme.breakpoints.down("xs")]: {
      width: "calc(100vw - 16px)",
      maxHeight: "calc(100vh - 16px)",
      margin: theme.spacing(1),
      borderRadius: 14,
    },
  },

  header: {
    position: "relative",
    padding: theme.spacing(2.5, 3, 2),
    background: "linear-gradient(60deg, #26c6da, #00acc1)",
    color: "#fff",
    flexShrink: 0,

    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 2, 1.5),
    },
  },

  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: "#fff",
  },

  title: {
    fontWeight: 600,
    fontSize: "1.25rem",
    marginBottom: theme.spacing(0.5),
    paddingRight: theme.spacing(5),
    wordBreak: "break-word",
    overflowWrap: "anywhere",

    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem",
    },

    [theme.breakpoints.down("xs")]: {
      fontSize: "1.1rem",
    },
  },

  subtitle: {
    opacity: 0.95,
    fontSize: "0.95rem",
    paddingRight: theme.spacing(5),
    wordBreak: "break-word",
    overflowWrap: "anywhere",

    [theme.breakpoints.down("xs")]: {
      fontSize: "0.9rem",
    },
  },

  permissionCode: {
    marginTop: theme.spacing(1.5),
    display: "inline-block",
    maxWidth: "100%",
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    backgroundColor: "rgba(255,255,255,0.14)",
    fontFamily: "monospace",
    fontSize: "0.9rem",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    whiteSpace: "normal",
  },

  content: {
    padding: theme.spacing(3),
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },

    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
    },
  },

  section: {
    marginBottom: theme.spacing(2.5),
  },

  sectionTitle: {
    color: "#4f749f",
    fontWeight: 600,
    marginBottom: theme.spacing(0.75),
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  sectionBody: {
    color: "#333",
    lineHeight: 1.7,
    whiteSpace: "pre-line",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  emptyState: {
    padding: theme.spacing(1, 0),
  },

  emptyTitle: {
    color: "#4f749f",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
}));

/**
 * Diálogo que muestra la ficha didáctica de un permiso Android
 * usando el namespace i18n "permissionsGuide".
 *
 * Props:
 * - open: boolean
 * - permissionKey: string normalizada, por ejemplo "READ_SMS"
 * - onClose: callback
 */
export default function PermissionGuideDialog({
  open,
  permissionKey,
  onClose,
}) {
  const classes = useStyles();

  /**
   * Este componente usa el namespace de guía.
   * Overview.js ya lo carga bajo demanda antes de abrir.
   */
  const { t, i18n } = useTranslation("permissionsGuide");

  /**
   * Si no hay permiso seleccionado, no renderizamos nada.
   */
  if (!permissionKey) {
    return null;
  }

  /**
   * Comprobamos si existe entrada real para ese permiso.
   * Así evitamos mostrar claves crudas de i18n.
   */
  const entryExists = i18n.exists(`permissions.${permissionKey}.description`, {
    ns: "permissionsGuide",
  });

  /**
   * Helper para leer campos del permiso seleccionado.
   */
  const getPermissionText = (field) =>
    t(`permissions.${permissionKey}.${field}`, {
      ns: "permissionsGuide",
      defaultValue: "",
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      aria-labelledby="permission-guide-dialog-title"
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <IconButton
          onClick={onClose}
          className={classes.closeButton}
          aria-label={t("actions.close", { defaultValue: "Cerrar" })}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          id="permission-guide-dialog-title"
          className={classes.title}
        >
          {t("title")}
        </Typography>

        <Typography className={classes.subtitle}>
          {t("subtitle")}
        </Typography>

        <Typography className={classes.permissionCode}>
          {permissionKey}
        </Typography>
      </div>

      <DialogContent dividers className={classes.content}>
        {entryExists ? (
          <>
            <Box className={classes.section}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t("sections.description")}
              </Typography>
              <Typography variant="body2" className={classes.sectionBody}>
                {getPermissionText("description")}
              </Typography>
            </Box>

            <Divider />

            <Box className={classes.section} mt={2.5}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t("sections.legitimateUse")}
              </Typography>
              <Typography variant="body2" className={classes.sectionBody}>
                {getPermissionText("legitimateUse")}
              </Typography>
            </Box>

            <Divider />

            <Box className={classes.section} mt={2.5}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t("sections.abuseRisk")}
              </Typography>
              <Typography variant="body2" className={classes.sectionBody}>
                {getPermissionText("abuseRisk")}
              </Typography>
            </Box>

            <Divider />

            <Box className={classes.section} mt={2.5} mb={0}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t("sections.goodPractices")}
              </Typography>
              <Typography variant="body2" className={classes.sectionBody}>
                {getPermissionText("goodPractices")}
              </Typography>
            </Box>
          </>
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant="subtitle1" className={classes.emptyTitle}>
              {t("empty.title")}
            </Typography>
            <Typography variant="body2" className={classes.sectionBody}>
              {t("empty.body")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

PermissionGuideDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  permissionKey: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

PermissionGuideDialog.defaultProps = {
  permissionKey: null,
};